/**
 * Public authentication, facility, RBAC, and settings contracts.
 */

import type { FirebaseOptions } from "firebase/app";

// ============================================================================
// Auth Wrapper Config (consumer-provided)
// ============================================================================

export interface AuthWrapperConfig {
  firebaseConfig: FirebaseOptions;
  apiUrls?: {
    authUrl?: string;
    backendServiceUrl?: string;
  };
  bearerAuth?: {
    username?: string;
    password?: string;
  };
  inactivityTimeoutMs?: number;
  authRedirectPath?: string;
  /** Settings route available for facility recovery after an RBAC 403. */
  facilityRecoverySettingsPath?: string;
  onAuthStateChange?: (user: AuthUser | null) => void;
  version?: string;
  analytics?: {
    setUser?: (user: { id: string; email: string }) => void;
    clearUser?: () => void;
  };
}

// ============================================================================
// Auth State Types
// ============================================================================

export type VerifiedStatus = "checking" | "verified" | "not-verified" | null;

export interface AuthUser {
  email: string | null;
  id: string;
  mfaEnabled?: boolean;
  mfaMethods?: string[];
}

export interface FirebaseAuthState {
  user: AuthUser | null;
  isLoading: boolean;
  signUpError: string | null;
  loginError: string | null;
  emailSentError: string | null;
  emailSent: boolean | null;
  passwordReset: boolean | null;
  passwordResetError: string | null;
  passwordResetInProgress: boolean | null;
  verifyingUser: VerifiedStatus;
  mfaSetupInProgress: boolean;
  mfaVerificationRequired: boolean;
  mfaError: string | null;
  mfaSetUpRequired: boolean;
}

export interface UserInput {
  email: string;
  password: string;
}

// ============================================================================
// Provider / Facility Types (inlined from risa-data-model to avoid dependency)
// ============================================================================

export interface Address {
  Address1?: string;
  Address2?: string;
  City?: string;
  State?: string;
  ZipCode?: string;
  Country?: string;
}

export interface PhoneNumber {
  AreaCityCode?: string;
  LocalNumber?: string;
}

export interface Provider {
  DocID?: string;
  NPI?: string;
  ID?: string;
  IDType?: string;
  FirstName?: string;
  LastName?: string;
  Credentials?: string[];
  Address?: Address;
  EmailAddresses?: string[];
  PhoneNumber?: PhoneNumber;
  FacilityId?: string;
  IsAdmin?: boolean;
  RoleId?: string;
  ProfilePicUrl?: string;
  LastPasswordUpdatedOn?: string;
  Status?: string;
}

export type ProviderWithId = Provider & {
  id: string;
};

/**
 * Shared identity and dashboard metadata for a healthcare facility.
 */
export interface HealthCareFacility {
  DocId?: string;
  ImageURL?: string;
  Name?: string;
  Address?: Address;
  PhoneNumber?: string;
  NPI?: string;
  TaxID?: string;
  FhirId?: string;
  ManagingOrganization?: string;
  dashboard_type?: DashboardType;
}

/** Dashboard families that can own a healthcare facility. */
export type DashboardType =
  | "med_onc"
  | "pharmacy"
  | "claims"
  | "clinical_trials";

/**
 * Feature flags and EV/BV validation toggles (nested under `feature_config` on facility docs).
 */
export interface HealthFacilityFeatureConfig {
  enable_review_mode: boolean;
  enable_oncoemr_tasks: boolean;
  validate_bv: boolean;
  validate_ev: boolean;
  /** When true, copay and co-insurance are required on EV coverage save/continue. */
  validate_copay_coinsurance: boolean;
  /**
   * When false, the browser-extension pairing section is hidden on the Profile page.
   * Missing/undefined means enabled (opt-out), so existing facilities keep current behavior.
   */
  enable_browser_extension?: boolean;
}

export interface HealthCareFacilityModel extends HealthCareFacility {
  client_id?: string;
  is_external_organization?: boolean;
  organization_id?: string;
  emr_name?: string;
  fhir_client?: string;
  RisaFaxNumber?: string;
  RisaPhoneNumber?: string;
  updated_at?: string;
  updated_by?: string;
  analytics_url?: string;
  /** Feature flags and EV/BV toggles (nested on the facility Firestore document). */
  feature_config?: HealthFacilityFeatureConfig;
  review_bo_status?: string;
  onco_emr_base_url?: string;
  inactive_insurance_bo_status?: string;
  auth_letter_type?: string;
  auth_status_filter?: string[];
  corresponding_internal_org_id?: string;
}

export interface FormOptionsModel {
  id?: string;
  data?: { label?: string; value?: string }[];
}

// ============================================================================
// Redux DataState helper
// ============================================================================

export interface DataState<T> {
  loading: boolean;
  error: string | null;
  data: T | null;
}

// ============================================================================
// RBAC / Member Management Types
// ============================================================================

/**
 * Stable role identifiers shared by auth APIs and dashboard member tooling.
 */
export enum RbacRoleId {
  SUPER_ADMIN = "super_admin",
  ADMIN_MED_ONC = "admin_med_onc",
  STANDARD_MED_ONC = "standard_med_onc",
  ADMIN_PHARMA = "admin_pharma",
  STANDARD_PHARMA = "standard_pharma",
  CLINICAL_TRIALS_USER = "clinical_trials_user",
  CLINICAL_TRIALS_ADMIN = "clinical_trials_admin",
}

/**
 * Facility-scoped roles accepted by member provisioning APIs.
 */
export type AssignableRoleId =
  | RbacRoleId.ADMIN_MED_ONC
  | RbacRoleId.STANDARD_MED_ONC
  | RbacRoleId.ADMIN_PHARMA
  | RbacRoleId.STANDARD_PHARMA
  | RbacRoleId.CLINICAL_TRIALS_USER
  | RbacRoleId.CLINICAL_TRIALS_ADMIN;

export interface TokenUserInfo {
  ProfilePicUrl: string;
  Status: string;
  healthcare_facilities?: string[];
  internal_healthcare_facilities?: string[];
  external_healthcare_facilities?: string[];
  IsAdmin: boolean;
  FacilityId: string;
  last_login: string;
  LastName: string;
  FirstName: string;
  EmailAddresses: string[];
  id: string;
}

export interface TokenFacilityInfo {
  id: string;
  name: string;
  all_facilities: string[];
  internal_facilities?: string[];
  external_facilities?: string[];
}

/**
 * Complete role-permission snapshot returned with a user token.
 */
export interface TokenPermissions {
  can_assign_roles: boolean;
  can_edit_configurations: boolean;
  can_delete_orders: boolean;
  can_view_configurations: boolean;
  can_view_rpa_config: boolean;
  can_view_credentials_config: boolean;
  can_manage_users: boolean;
  can_edit_own_orders: boolean;
  can_assign_orders: boolean;
  can_edit_all_orders: boolean;
  can_view_own_orders: boolean;
  can_access_external_orgs: boolean;
  can_view_health_facility_config: boolean;
  can_view_form_config: boolean;
  can_export_data: boolean;
  can_manage_org_users: boolean;
  can_reassign_orders: boolean;
  can_import_data: boolean;
  is_read_only: boolean;
  can_view_all_orders: boolean;
  can_access_all_orgs: boolean;
  can_switch_orgs: boolean;
  can_manage_access_control: boolean;
  can_view_clinical_trials: boolean;
  can_admin_clinical_trials: boolean;
}

export interface TokenRoleInfo {
  id: string;
  name: string;
  description: string;
  type: string;
  permissions: TokenPermissions;
  created_at: string;
  updated_at: string;
}

/**
 * Second-factor gate signal on a token-exchange response. `null`/absent means a
 * full token was issued. `"required"` means the user must submit a TOTP code;
 * `"enrollment"` means the user must enroll a TOTP factor first.
 */
export type MfaStatus = "required" | "enrollment" | null;

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  /** Set when the second factor is not yet satisfied; `access_token` is then empty. */
  mfa_status?: MfaStatus;
  /** Short-lived challenge token to present to the verify-totp endpoint. */
  mfa_token?: string | null;
  /** otpauth:// provisioning URI for QR rendering (only on `mfa_status === "enrollment"`). */
  otpauth_uri?: string | null;
  user: TokenUserInfo;
  facility: TokenFacilityInfo;
  role: TokenRoleInfo;
}

/**
 * Snapshot of the pending second-factor challenge, surfaced by the token
 * manager so the UI can route to the enroll / verify screens.
 */
export interface MfaChallengeState {
  status: MfaStatus;
  mfaToken: string | null;
  otpauthUri: string | null;
}

export interface StoredTokenData {
  token: string;
  expirationTime: number;
  facilityId: string;
  user: TokenUserInfo;
  facility: TokenFacilityInfo;
  role: TokenRoleInfo;
}

export interface InviteMemberPayload {
  name: string;
  email: string;
  roleId: AssignableRoleId;
}

export interface InviteMemberResponse {
  success: boolean;
  message: string;
  user_id: string | null;
  invitation_id: string | null;
  is_new_user: boolean;
}

export interface RemoveMemberResponse {
  success: boolean;
  message: string;
}

export interface ResetTwoFactorResponse {
  success: boolean;
  message: string;
  user_id: string;
}

export interface DeleteMemberResponse {
  success: boolean;
  message: string;
  user_id: string;
}

export interface UpdateMemberRolePayload {
  roleId: AssignableRoleId;
}

export interface UpdateMemberRoleResponse {
  success: boolean;
  message: string;
}

export interface MemberManagementError {
  status: number;
  message: string;
}

export interface FacilityRoleAssignment {
  facilityId: string;
  roleId: AssignableRoleId;
}

export interface BulkUpdatePayload {
  userId?: string;
  email?: string;
  name?: string;
  internalFacilities?: FacilityRoleAssignment[];
  externalFacilities?: FacilityRoleAssignment[];
}

export interface BulkUpdateFacilityResult {
  facility_id: string;
  success: boolean;
  message: string;
  is_new_user: boolean;
  invitation_id?: string;
}

export interface BulkUpdateResponse {
  success: boolean;
  message: string;
  user_id: string;
  results: BulkUpdateFacilityResult[];
}

export interface ProviderWithFacilities {
  id: string;
  name: string;
  email: string;
  status: string;
  internalFacilityIds: string[];
  externalFacilityIds: string[];
  internalFacilityNames: string[];
  externalFacilityNames: string[];
  facilityRoles: Record<string, AssignableRoleId>;
}

export interface FacilityOption {
  id: string;
  name: string;
  isExternal: boolean;
}

export type RbacTokenErrorType =
  | "inactive_account"
  | "unauthorized"
  | "forbidden"
  | "unknown";

export interface RbacTokenResult {
  success: boolean;
  errorType?: RbacTokenErrorType;
  errorMessage?: string;
}

export type FacilityType = "internal" | "external" | "not_assigned";

// ============================================================================
// useAuth hook return type
// ============================================================================

export interface UseAuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  profilePicUrl: string;
  status: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

export interface UseAuthFacility {
  id: string;
  name: string;
  type: FacilityType;
  internalFacilities: string[];
  externalFacilities: string[];
}

export interface UseAuthRole {
  id: string;
  name: string;
  permissions: TokenPermissions;
}

export interface UseAuthResult {
  isAuthenticated: boolean;
  isLoading: boolean;
  verifyingUser: VerifiedStatus;
  logout: () => Promise<void>;
  user: UseAuthUser | null;
  facility: UseAuthFacility | null;
  role: UseAuthRole | null;
}

// ============================================================================
// Settings Page Props
// ============================================================================

export type SettingsPageId =
  | "profile"
  | "security"
  | "members"
  | "support"
  | "superadmin";

export interface RoleOption {
  value: string;
  label: string;
}

export interface SettingsPageProps {
  enabledPages?: SettingsPageId[];
  roleOptions?: RoleOption[];
}

// ============================================================================
// Auth Error Page
// ============================================================================

export interface AuthError {
  type: RbacTokenErrorType;
  title: string;
  message: string;
}

// ============================================================================
// Super Admin Types (for Redux slice)
// ============================================================================

export interface SuperAdminProvider {
  uid?: string;
  id: string;
  FirstName?: string;
  LastName?: string;
  EmailAddresses?: string[];
  Status?: string;
  IsAdmin?: boolean;
  FacilityId?: string;
  internal_healthcare_facilities?: string[];
  external_healthcare_facilities?: string[];
  healthcare_facilities?: string[];
  role_id?: string;
}

/**
 * Minimal facility metadata used by super-admin member assignment.
 */
export interface FacilityInfo {
  id: string;
  name: string;
  isExternal: boolean;
  dashboardType?: DashboardType;
}

export interface SuperAdminProvidersState {
  providers: SuperAdminProvider[];
  facilities: FacilityInfo[];
  loading: boolean;
  error: string | null;
}
